import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { GET_SALON_STOREFRONT, REMOVE_HERO_BANNER, SET_HERO_BANNER } from "@/graphql/salon";
import { uploadAsset } from "@/lib/asset/upload-asset";
import { useSalonStore } from "@/lib/store/salon";

const ACCEPTED_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

type AcceptedContentType = (typeof ACCEPTED_CONTENT_TYPES)[number];

function isAcceptedContentType(value: string): value is AcceptedContentType {
  return (ACCEPTED_CONTENT_TYPES as readonly string[]).includes(value);
}

export function useHeroBanner() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id ?? "";
  const [uploading, setUploading] = useState(false);
  const apolloClient = useApolloClient();

  const { data, loading, error, refetch } = useQuery(GET_SALON_STOREFRONT, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const [setBanner] = useMutation(SET_HERO_BANNER);
  const [removeBanner] = useMutation(REMOVE_HERO_BANNER);

  const upload = useCallback(
    async (file: File) => {
      if (!salonId) {
        toast.error("No salon selected. Please select a salon before uploading.");
        return;
      }

      if (!isAcceptedContentType(file.type)) {
        toast.error("Unsupported image type. Please upload PNG, JPEG, or WebP.");
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error("Image is too large. Maximum size is 10 MB.");
        return;
      }

      setUploading(true);
      try {
        const { storageKey } = await uploadAsset(apolloClient, {
          file,
          kind: "HERO_BANNER",
          salonId,
        });

        await setBanner({
          variables: { salonId, storageKey },
        });

        await refetch();
        toast.success("Hero banner uploaded. Thumbnail is being generated.");
      } catch (err) {
        console.error("Hero banner upload failed", err);
        toast.error(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [salonId, apolloClient, setBanner, refetch]
  );

  const remove = useCallback(async () => {
    if (!salonId) return;
    try {
      await removeBanner({ variables: { salonId } });
      await refetch();
      toast.success("Hero banner removed");
    } catch (err) {
      console.error("Remove hero banner failed", err);
      toast.error(err instanceof Error ? err.message : "Failed to remove banner");
    }
  }, [salonId, removeBanner, refetch]);

  return {
    heroBannerUrl: data?.getSalon?.heroBannerUrl ?? null,
    heroBannerThumbnailUrl: data?.getSalon?.heroBannerThumbnailUrl ?? null,
    hasBanner: !!data?.getSalon?.heroBannerStorageKey,
    loading,
    uploading,
    error,
    upload,
    remove,
  };
}
